import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout from '../../components/ui/Layout';
import { SITE_URL, SITE_NAME, getProductShareUrl, getProductShareSlug } from '../../constants/site';
import ShareButton from '../../components/ui/ShareButton';
import { db } from '../../firebase';
import { collection, query, where, getDocs, limit, doc, getDoc } from 'firebase/firestore'; // Removed orderBy as it might not be used or requires specific indexes with array-contains
import { Container, Row, Col, Card, Spinner, Alert, Button } from 'react-bootstrap';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { getStorage, ref as storageFBRef, getDownloadURL } from 'firebase/storage';
import { createLogger } from '../../utils/logger';

const logger = createLogger('SearchResultsPage');

const ResultImage = ({ imagePath, altText, type }) => {
  const [imageUrl, setImageUrl] = useState('');
  const storage = getStorage();
  useEffect(() => {
    setImageUrl('');
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:') && !imagePath.startsWith('http')) {
      const imageRef = storageFBRef(storage, imagePath);
      getDownloadURL(imageRef).then(setImageUrl).catch(err => logger.error(`Error fetching ${type} image URL:`, err));
    } else if (imagePath && imagePath.startsWith('http')) {
        setImageUrl(imagePath);
    } else {
        logger.warn("Invalid imagePath for ResultImage:", imagePath);
    }
  }, [imagePath, storage, type]);

  if (!imageUrl) return <div className="card-img-top" style={{ height: type === 'product' ? '180px' : '200px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span>No Image</span></div>;
  return <img src={imageUrl} className="card-img-top" alt={altText} style={{ height: type === 'product' ? '180px' : '200px', objectFit: 'cover' }} />;
};

export default function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchQuery = searchParams.get('q');

  const [supermarketResults, setSupermarketResults] = useState([]);
  const [productResults, setProductResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [productSupermarkets, setProductSupermarkets] = useState({}); // productId -> [supermarkets]

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { label: searchQuery ? `Search: "${searchQuery}"` : "Search Results" }
  ];

  const performSearch = useCallback(async () => {
    if (!searchQuery || searchQuery.trim() === '') {
      setLoading(false);
      setSupermarketResults([]);
      setProductResults([]);
      setError('Please enter a search term.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const searchTermProcessed = searchQuery.toLowerCase().trim();
      const searchTermsArray = searchTermProcessed.split(/\s+/).filter(term => term.length > 0);

      // Supermarket Search (still basic prefix scan on display_name, can be enhanced with keywords later)
      const supermarketQuery = query(
        collection(db, 'Supermarkets'), 
        where('display_name', '>=', searchQuery), // Original case for prefix scan often works better
        where('display_name', '<=', `${searchQuery  }\uf8ff`),
        limit(10)
      );

      let productPromise = Promise.resolve({ docs: [] }); // Default to empty result
      if (searchTermsArray.length > 0) {
        // Product Search using search_keywords and array-contains-any
        // Note: array-contains-any is limited to 10 terms in searchTermsArray
        const productKeywordsQuery = query(
          collection(db, 'Products'), 
          where('search_keywords', 'array-contains-any', searchTermsArray.slice(0, 10)),
          limit(20) // Increased limit for products
        );
        productPromise = getDocs(productKeywordsQuery);
      }
      
      const [supermarketSnap, productSnap] = await Promise.all([
        getDocs(supermarketQuery),
        productPromise
      ]);

      // For supermarkets, client-side filter for better matching if prefix scan is too broad
      let supermarkets = supermarketSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      supermarkets = supermarkets.filter(s => 
        s.display_name?.toLowerCase().includes(searchTermProcessed)
      );
      setSupermarketResults(supermarkets);

      // Product results are directly from the keyword search
      const products = productSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProductResults(products);

      if (supermarkets.length === 0 && products.length === 0) {
        setError(`No results found for "${searchQuery}".`);
      }

    } catch (err) {
      logger.error("Error performing search:", err);
      setError(`Failed to perform search. ${  err.message}`);
    }
    setLoading(false);
  }, [searchQuery]);

  // Fetch supermarkets for each product result
  useEffect(() => {
    const fetchSupermarketsForProducts = async () => {
      if (productResults.length === 0) {
        setProductSupermarkets({});
        return;
      }
      const result = {};
      await Promise.all(productResults.map(async (product) => {
        // Find up to 2 supermarkets with this product in inventory
        const inventoryRef = collection(db, 'Inventory');
        const inventoryQuery = query(
          inventoryRef,
          where('product_id', '==', product.id),
          where('quantity', '>', 0),
          limit(2)
        );
        const inventorySnapshot = await getDocs(inventoryQuery);
        const supermarketIds = inventorySnapshot.docs.map(doc => doc.data().supermarket_id);
        let supermarketsForProduct = [];
        if (supermarketIds.length > 0) {
          const supermarketDocs = await Promise.all(
            supermarketIds.map(id => getDoc(doc(db, 'Supermarkets', id)))
          );
          supermarketsForProduct = supermarketDocs
            .filter(doc => doc.exists)
            .map(doc => ({ id: doc.id, ...doc.data() }));
        }
        result[product.id] = supermarketsForProduct;
      }));
      setProductSupermarkets(result);
    };
    fetchSupermarketsForProducts();
  }, [productResults]);

  useEffect(() => {
    performSearch();
  }, [performSearch]);

  if (loading) return <Layout><Container className="mt-4 text-center"><Spinner animation="border"/> <p>Searching...</p></Container></Layout>;

  const seoTitle = searchQuery ? `Search: "${searchQuery}" | ${SITE_NAME}` : `Search | ${SITE_NAME}`;
  const seoDescription = searchQuery
    ? `Search results for "${searchQuery}" — find products and stores on ${SITE_NAME}.`
    : `Search for products and stores on ${SITE_NAME}.`;
  const seoUrl = `${SITE_URL}/search${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`;
  const seoImage = `${SITE_URL}/logo192.png`;

  return (
    <Layout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
      </Helmet>
      <Container className="mt-4">
        <Row className="align-items-center mb-1">
            <Col xs="auto">
                <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
                    &larr; Go Back
                </Button>
            </Col>
            <Col>
                <BreadcrumbNavigation items={breadcrumbItems} />
            </Col>
        </Row>
        <h1 className="mb-3 mt-3">Search Results {searchQuery && <small className="text-muted">for "{searchQuery}"</small>}</h1>

        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && supermarketResults.length === 0 && productResults.length === 0 && !error && (
          <Alert variant="info">No results found for "{searchQuery}". Try a different search term.</Alert>
        )}

        {supermarketResults.length > 0 && (
          <section className="mb-5">
            <h2>Matching hardware stores ({supermarketResults.length})</h2>
            <Row xs={1} md={2} lg={3} className="g-4">
              {supermarketResults.map(s => (
                <Col key={s.id}>
                  <Card className="h-100 shadow-sm">
                    <Link to={`/hardware/${s.id}/products`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      {s.media && s.media.length > 0 && s.media[0].url ? (
                        <ResultImage imagePath={s.media[0].url} altText={s.display_name} type="supermarket" />
                      ) : (
                        <div className="card-img-top" style={{height: '200px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><span>No Image</span></div>
                      )}
                      <Card.Body>
                        <Card.Title>{s.display_name}</Card.Title>
                        {s.address && <Card.Text className="small text-muted">{s.address}</Card.Text>}
                      </Card.Body>
                    </Link>
                    <Card.Footer className="bg-transparent border-top-0 pt-0">
                      <ShareButton
                        url={s.seo_name ? `${SITE_URL}/hardware/${s.id}/products/${s.seo_name}` : `${SITE_URL}/hardware/${s.id}/products`}
                        title={`${s.display_name} - Products | ${SITE_NAME}`}
                        text={`Shop groceries from ${s.display_name}`}
                        variant="outline-secondary"
                        size="sm"
                        showLabel={false}
                      />
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>
        )}

        {productResults.length > 0 && (
          <section>
            <h2>Matching Products ({productResults.length})</h2>
            <Row xs={1} sm={2} md={3} lg={4} className="g-4">
              {productResults.map(p => (
                <Col key={p.id}>
                  <Card className="h-100 shadow-sm">
                    {/* Product card content */}
                    {p.media && p.media.length > 0 && p.media[0].url ? (
                        <ResultImage imagePath={p.media[0].url} altText={p.display_name} type="product" />
                    ) : (
                        <div className="card-img-top" style={{height: '180px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'}}><span>No Image</span></div>
                    )}
                    <Card.Body>
                        <Card.Title style={{fontSize: '1rem'}}>{p.display_name}</Card.Title>
                        {/* Display tags if available */}
                        {p.tags && Array.isArray(p.tags) && p.tags.length > 0 && 
                            <p className="small text-muted mb-1">Tags: {p.tags.join(', ')}</p>}
                        {/* Available at supermarkets */}
                        <div className="mt-2">
                          {productSupermarkets[p.id] && productSupermarkets[p.id].length > 0 ? (
                            <>
                              <div className="mb-1" style={{fontSize: '0.95em'}}>Available at:</div>
                              {productSupermarkets[p.id].slice(0,2).map(sup => (
                                <div key={sup.id}>
                                  <Link
                                    to={`/hardware/${sup.id}/products/${getProductShareSlug(p)}`}
                                    style={{textDecoration: 'underline', color: '#1a7f37'}}
                                  >
                                    {sup.display_name || 'Supermarket'}
                                  </Link>
                                </div>
                              ))}
                            </>
                          ) : (
                            <div className="text-muted" style={{fontSize: '0.95em'}}>Not available in hardware stores</div>
                          )}
                        </div>
                    </Card.Body>
                    <Card.Footer className="bg-transparent border-top-0 pt-0">
                      <ShareButton
                        url={getProductShareUrl(p)}
                        title={`${p.display_name} | ${SITE_NAME}`}
                        text={`Check out ${p.display_name} on ${SITE_NAME}`}
                        variant="outline-secondary"
                        size="sm"
                        showLabel={false}
                      />
                    </Card.Footer>
                  </Card>
                </Col>
              ))}
            </Row>
          </section>
        )}
      </Container>
    </Layout>
  );
}
